import React from 'react';
import {
  Box, Typography, Paper, TextField, FormControl, InputLabel, Select, MenuItem,
  Grid, Snackbar, Alert, CircularProgress, Avatar,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';

import { PageHeader, FieldRow, SaveBar } from '../ui';
import useUserProfilePage from './useUserProfilePage';

const UserProfile = () => {
  const p = useUserProfilePage();

  if (p.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
      <PageHeader title={p.t('My Profile')} />

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ width: 80, height: 80, mr: 2, bgcolor: 'primary.main' }}>
            <AccountCircleIcon />
          </Avatar>
          <Box>
            <Typography variant="h5">{p.form.username || p.t('User')}</Typography>
            <Typography variant="body2" color="text.secondary">{p.form.email || p.t('No email provided')}</Typography>
          </Box>
        </Box>

        <FieldRow label={p.t('Username')} required>
          <TextField
            fullWidth
            value={p.form.username}
            onChange={(e) => p.setForm({ ...p.form, username: e.target.value })}
          />
        </FieldRow>

        <FieldRow label={p.t('Email')}>
          <TextField
            fullWidth
            type="email"
            value={p.form.email}
            onChange={(e) => p.setForm({ ...p.form, email: e.target.value })}
          />
        </FieldRow>

        <FieldRow>
          <FormControl fullWidth>
            <InputLabel>{p.t('Preferred Language')}</InputLabel>
            <Select
              value={p.form.preferredLanguage}
              onChange={(e) => p.setForm({ ...p.form, preferredLanguage: e.target.value })}
              label={p.t('Preferred Language')}
            >
              {p.availableLanguages.map(([code, name]) => (
                <MenuItem key={code} value={code}>{p.t(name)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </FieldRow>

        {p.canEditShipping && (
          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>{p.t('Shipping Information')}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FieldRow label={p.t('Recipient Name')}>
                  <TextField
                    fullWidth
                    value={p.form.shipping.recipientName}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, recipientName: e.target.value } })}
                    inputProps={{ maxLength: 100 }}
                  />
                </FieldRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldRow label={p.t('Phone Number')}>
                  <TextField
                    fullWidth
                    value={p.form.shipping.phone}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, phone: e.target.value } })}
                    inputProps={{ maxLength: 30 }}
                  />
                </FieldRow>
              </Grid>
              <Grid item xs={12}>
                <FieldRow label={p.t('Address Line 1')}>
                  <TextField
                    fullWidth
                    value={p.form.shipping.addressLine1}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, addressLine1: e.target.value } })}
                    inputProps={{ maxLength: 200 }}
                  />
                </FieldRow>
              </Grid>
              <Grid item xs={12}>
                <FieldRow label={p.t('Address Line 2')}>
                  <TextField
                    fullWidth
                    value={p.form.shipping.addressLine2}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, addressLine2: e.target.value } })}
                    inputProps={{ maxLength: 200 }}
                  />
                </FieldRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldRow label={p.t('City')}>
                  <TextField
                    fullWidth
                    value={p.form.shipping.city}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, city: e.target.value } })}
                    inputProps={{ maxLength: 100 }}
                  />
                </FieldRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldRow label={p.t('State / Province')}>
                  <TextField
                    fullWidth
                    value={p.form.shipping.state}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, state: e.target.value } })}
                    inputProps={{ maxLength: 100 }}
                  />
                </FieldRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldRow label={p.t('Postal Code')}>
                  <TextField
                    fullWidth
                    value={p.form.shipping.postalCode}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, postalCode: e.target.value } })}
                    inputProps={{ maxLength: 20 }}
                  />
                </FieldRow>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FieldRow label={p.t('Country')}>
                  <TextField
                    fullWidth
                    value={p.form.shipping.country}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, country: e.target.value } })}
                    inputProps={{ maxLength: 100 }}
                  />
                </FieldRow>
              </Grid>
              <Grid item xs={12}>
                <FieldRow label={p.t('Delivery Notes')}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={p.form.shipping.deliveryNotes}
                    onChange={(e) => p.setForm({ ...p.form, shipping: { ...p.form.shipping, deliveryNotes: e.target.value } })}
                    inputProps={{ maxLength: 500 }}
                  />
                </FieldRow>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Paper>

      <SaveBar
        dirty={p.isDirty}
        saving={p.saving}
        message={p.t('You have unsaved changes', { defaultValue: 'You have unsaved changes' })}
        discardLabel={p.t('Discard', { defaultValue: 'Discard' })}
        saveLabel={p.saving ? p.t('Saving...') : p.t('Save Changes')}
        onDiscard={p.discardChanges}
        onSave={p.handleSave}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={6000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>{p.snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;
